import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Alert,
  Card,
  CardContent,
  CircularProgress,
  Backdrop
} from '@mui/material';
import {
  AdminPanelSettings as AdminIcon,
  Email as EmailIcon,
  Lock as LockIcon,
  Person as PersonIcon,
  CheckCircle as CheckIcon,
  Security as SecurityIcon
} from '@mui/icons-material';
import { registerUser } from '../services/authService';
import { UserRole } from '../utils/types';
import { useNavigate } from 'react-router-dom';
import { needsInitialSetup, backupAdminData } from '../services/setupService';

const QuickSetupPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [checkingSetup, setCheckingSetup] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [setupRequired, setSetupRequired] = useState(false);
  
  const [formData, setFormData] = useState({
    name: 'مدير النظام',
    email: 'admin@baballouk0.com',
    password: 'Admin123!',
    confirmPassword: 'Admin123!'
  });

  // فحص الحاجة للإعداد الأولي عند تحميل الصفحة
  useEffect(() => {
    const checkInitialSetup = async () => {
      try {
        // تم تعطيل صفحة الإعداد - Setup page disabled
        console.log('صفحة الإعداد معطلة، التوجيه إلى صفحة تسجيل الدخول');
        navigate('/login', { replace: true });
        return;
        
        // console.log('فحص الحاجة للإعداد الأولي...');
        // const needsSetup = await needsInitialSetup();
        // 
        // if (!needsSetup) {
        //   // إذا كان هناك مدير موجود، توجيه إلى صفحة تسجيل الدخول
        //   console.log('يوجد مدير في النظام، التوجيه إلى صفحة تسجيل الدخول');
        //   navigate('/login', { replace: true });
        //   return;
        // }
        // 
        // console.log('النظام يحتاج إلى إعداد أولي');
        // setSetupRequired(true);
      } catch (error) {
        console.error('خطأ في فحص الإعداد الأولي:', error);
        // في حالة الخطأ، اسمح بالإعداد
        setSetupRequired(true);
      } finally {
        setCheckingSetup(false);
      }
    };

    checkInitialSetup();
  }, [navigate]);

  const handleInputChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    setError(null);
  };

  const handleCreateAdmin = async () => {
    try {
      setLoading(true);
      setError(null);

      // التحقق من صحة البيانات
      if (!formData.name.trim()) {
        setError('يرجى إدخال اسم المدير');
        return;
      }

      if (!formData.email.trim()) {
        setError('يرجى إدخال البريد الإلكتروني');
        return;
      }

      if (formData.password.length < 6) {
        setError('كلمة المرور يجب أن تكون 6 أحرف على الأقل');
        return;
      }

      if (formData.password !== formData.confirmPassword) {
        setError('كلمة المرور وتأكيدها غير متطابقتين');
        return;
      }

      // التحقق مرة أخرى من عدم وجود مدير (حماية إضافية)
      const stillNeedsSetup = await needsInitialSetup();
      if (!stillNeedsSetup) {
        setError('يوجد مدير في النظام بالفعل. سيتم توجيهك إلى صفحة تسجيل الدخول.');
        setTimeout(() => {
          navigate('/login');
        }, 2000);
        return;
      }

      console.log('إنشاء حساب المدير الأول...');
      const user = await registerUser(
        formData.email,
        formData.password,
        formData.name,
        UserRole.ADMIN
      );

      console.log('تم إنشاء حساب المدير بنجاح:', user);
      
      // إنشاء نسخة احتياطية فورية
      try {
        await backupAdminData(user.id);
        console.log('تم إنشاء نسخة احتياطية لحساب المدير');
      } catch (backupError) {
        console.warn('تحذير: فشل في إنشاء النسخة الاحتياطية:', backupError);
      }
      
      setSuccess(true);

      // انتظار قصير ثم التوجه لصفحة تسجيل الدخول
      setTimeout(() => {
        navigate('/login');
      }, 3000);

    } catch (err: any) {
      console.error('خطأ في إنشاء حساب المدير:', err);
      setError(err.message || 'فشل في إنشاء حساب المدير');
    } finally {
      setLoading(false);
    }
  };

  // شاشة فحص الإعداد الأولي
  if (checkingSetup) {
    return (
      <Backdrop open={true} sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <Box sx={{ textAlign: 'center' }}>
          <SecurityIcon sx={{ fontSize: 80, mb: 3, color: '#667eea' }} />
          <Typography variant="h5" gutterBottom>
            فحص الإعداد الأولي للنظام...
          </Typography>
          <CircularProgress size={60} sx={{ color: '#667eea' }} />
        </Box>
      </Backdrop>
    );
  }

  // إذا لم يكن الإعداد مطلوباً، إظهار رسالة وتوجيه
  if (!setupRequired) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <CheckIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h5" gutterBottom color="success.main">
              النظام جاهز للاستخدام!
            </Typography>
            <Typography variant="body1" color="text.secondary">
              يوجد مدير في النظام بالفعل. سيتم توجيهك إلى صفحة تسجيل الدخول...
            </Typography>
          </CardContent>
        </Card>
      </Container>
    );
  }

  if (success) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <AdminIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h5" gutterBottom color="success.main">
              تم إنشاء حساب المدير بنجاح!
            </Typography>
            <Typography variant="body1" color="text.secondary">
              سيتم توجيهك إلى صفحة تسجيل الدخول...
            </Typography>
          </CardContent>
        </Card>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <AdminIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
            <Typography variant="h4" gutterBottom>
              إنشاء حساب المدير
            </Typography>
            <Typography variant="body1" color="text.secondary">
              إنشاء حساب المدير الأول للنظام
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <TextField
            fullWidth
            label="اسم المدير"
            value={formData.name}
            onChange={handleInputChange('name')}
            margin="normal"
            InputProps={{
              startAdornment: <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
            }}
          />
          
          <TextField
            fullWidth
            label="البريد الإلكتروني"
            type="email"
            value={formData.email}
            onChange={handleInputChange('email')}
            margin="normal"
            InputProps={{
              startAdornment: <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />
            }}
          />
          
          <TextField
            fullWidth
            label="كلمة المرور"
            type="password"
            value={formData.password}
            onChange={handleInputChange('password')}
            margin="normal"
            InputProps={{
              startAdornment: <LockIcon sx={{ mr: 1, color: 'text.secondary' }} />
            }}
          />
          
          <TextField
            fullWidth
            label="تأكيد كلمة المرور"
            type="password"
            value={formData.confirmPassword}
            onChange={handleInputChange('confirmPassword')}
            margin="normal"
            InputProps={{
              startAdornment: <LockIcon sx={{ mr: 1, color: 'text.secondary' }} />
            }}
          />

          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={handleCreateAdmin}
            disabled={loading}
            sx={{ mt: 3 }}
            startIcon={loading ? <CircularProgress size={20} /> : <AdminIcon />}
          >
            {loading ? 'جاري إنشاء الحساب...' : 'إنشاء حساب المدير'}
          </Button>

          <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>البيانات الافتراضية:</strong><br/>
              البريد الإلكتروني: admin@fixled.com<br/>
              كلمة المرور: Admin123!
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};

export default QuickSetupPage;
